import React, { useState } from 'react';
import Button from '../button/button';
import { qaAttribute } from '../utils/qa-service';
import {
    ModalHeader,
    ModalSearchBar,
    NoResultsScreen,
    FixedSelectModalBody,
    FixedSelectModalClosePanel,
    FixedSelectModalLightbox,
} from './select-modal.style';
import RadioList, { ListData } from '../radio-list/radio-list';
import debounce from '../utils/debounce';
import ClearButton, { useHandleClearButton } from '../input-clear-button/input-clear-button';
import Icon from '../icon/icon';

const SelectModal = ({
    elName,
    selectedValue,
    searchable = true,
    isModalOpen,
    onCloseModal,
    noResultsImg,
    tooltipMessage,
    listTitle,
    listData,
    listName,
    modalTitle,
    placeholder,
    ga4EventSearch,
    onSave,
}: {
    elName: string;
    selectedValue: string | undefined;
    searchable?: boolean;
    isModalOpen: boolean;
    onCloseModal: Function;
    noResultsImg;
    tooltipMessage;
    listTitle: string;
    listData: ListData;
    listName: string;
    modalTitle;
    placeholder;
    ga4EventSearch;
    onSave;
}) => {
    const [internalSelectedValue, setInternalSelectedValue] = useState(selectedValue);
    const [internalSelectedDisplayName, setInternalSelectedDisplayName] = useState('');

    const [data, setData] = useState(listData);
    const [radioListTitle, setRadioListTitle] = useState(listTitle);
    const [noResults, setNoResults] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const handleFilter = (
        noResults: boolean,
        data: ListData = listData,
        title: string = listTitle
    ) => {
        setData(data);
        setRadioListTitle(title);
        setNoResults(noResults);
    };

    const filterData = (searchTerm) => {
        const filteredData = listData.filter((breed) =>
            breed.displayValue.toLowerCase().includes(searchTerm.toLowerCase())
        );
        handleFilter(filteredData.length === 0, filteredData, '');
    };

    const handleInput = debounce((e) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(() => {
            filterData(newSearchTerm);
            return newSearchTerm;
        });
    }, 300);

    const handleClear = () => {
        onClickClear();
        handleFilter(false);
        setSearchTerm('');
    };

    const { inputRef, showClear, onClickClear } = useHandleClearButton('adTitle');

    const modalHeader = () => (
        <ModalHeader searchable={searchable}>
            <h2 {...qaAttribute(`${elName}-modal-title`)}>{modalTitle}</h2>
            {searchable && (
                <ModalSearchBar>
                    <Icon type="magnifying-glass" size="medium" />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={placeholder}
                        onInput={(e) => handleInput(e)}
                        onBlur={() => ga4EventSearch()}
                        {...qaAttribute(`${elName}-search-input`)}
                    />
                    {showClear && <ClearButton onClick={handleClear} />}
                </ModalSearchBar>
            )}
        </ModalHeader>
    );

    const handleSave = () => {
        onSave(internalSelectedValue, internalSelectedDisplayName);
    };

    return (
        <FixedSelectModalLightbox
            isOpen={isModalOpen}
            onClose={async () => {
                onCloseModal();
            }}
            title={modalHeader()}
            displayAsSheetOnMweb
        >
            <FixedSelectModalClosePanel>
                <div>
                    <Button
                        display="primary"
                        label="Save"
                        onClick={async (e) => {
                            e.preventDefault();
                            handleSave();
                        }}
                        className={internalSelectedValue ? 'active' : ''}
                        {...qaAttribute(`${elName}-modal-save-btn`)}
                        disabled={!internalSelectedValue}
                    />
                </div>
            </FixedSelectModalClosePanel>
            <FixedSelectModalBody>
                {noResults ? (
                    <NoResultsScreen {...qaAttribute('no-results-screen')}>
                        {noResultsImg}
                        <p>
                            No search results for <br /> '{searchTerm}'
                        </p>
                    </NoResultsScreen>
                ) : (
                    <RadioList
                        title={radioListTitle}
                        showTitle={searchable}
                        data={data}
                        listName={listName}
                        elName={elName}
                        selectedValue={internalSelectedValue || selectedValue}
                        setSelectedValue={setInternalSelectedValue}
                        setSelectedDisplayName={setInternalSelectedDisplayName}
                        searchTerm={searchTerm}
                        tooltipMessage={tooltipMessage}
                    />
                )}
            </FixedSelectModalBody>
        </FixedSelectModalLightbox>
    );
};

export default SelectModal;
